import React, { useState, useContext, useEffect } from 'react';
import { Modal, Button, Form, Alert, ProgressBar, Row, Col } from 'react-bootstrap';
import DragDropImageUpload from './DragDropImageUpload';
import Turnstile from 'react-turnstile';
import { toast } from 'react-toastify';
import { FaTrash, FaTv } from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import Select from 'react-select/creatable';
import { v4 as uuidv4 } from 'uuid';

const API_URL = process.env.REACT_APP_API_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const CRTSubmissionModal = ({ show, onHide, existingCrtTitle = '', existingCrtId = null, existingCrtSlug = null, imageOnly = false, submissionType = 'new' }) => {
  const { isAuthenticated, token, name } = useContext(AuthContext);
  const initialFormState = {
    title: existingCrtTitle || '',
    author: '',
    author_email: '',
    chassis: '',
    viewable_size: '',
    chassis_stereo: '',
    inputs: [],
    chroma: '',
    osd: '',
    tubes: [],
    manual_links: [],
    notes: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);

  const [tempSlug, setTempSlug] = useState('');

  const [titleSuggestions, setTitleSuggestions] = useState([]);
  const [isLoadingTitles, setIsLoadingTitles] = useState(false);

  const [selectedExistingCrt, setSelectedExistingCrt] = useState(null);

  // Track if we're contributing to existing CRT
  const [isContribution, setIsContribution] = useState(false);

  // Generate viewable size options from 5" to 40"
  const viewableSizeOptions = Array.from({ length: 36 }, (_, i) => ({
    value: String(i + 5),
    label: `${i + 5}"`
  }));

  const inputOptions = [
    { value: 'Composite', label: 'Composite' },
    { value: 'Component (YPbPr)', label: 'Component (YPbPr)' },
    { value: 'S-Video', label: 'S-Video' },
    { value: 'RF', label: 'RF' },
    { value: 'RGB (BNC)', label: 'RGB (BNC)' },
    { value: 'RGB (SCART)', label: 'RGB (SCART)' },
    { value: 'RGB (Other)', label: 'RGB (Other)' }
  ];

  // Update form data when existingCrtTitle changes
  useEffect(() => {
    if (existingCrtTitle) {
      setFormData(prev => ({
        ...prev,
        title: existingCrtTitle
      }));
    }
  }, [existingCrtTitle]);

  // Update temp slug when title changes
  useEffect(() => {
    const timestamp = Date.now();
    const baseSlug = formData.title.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    setTempSlug(baseSlug ? `${baseSlug}-${timestamp}` : `temp-${timestamp}-${uuidv4()}`);
  }, [formData.title]);

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch CRT title suggestions
  const fetchTitleSuggestions = async (inputValue) => {
    if (inputValue.length < 2) {
      setTitleSuggestions([]);
      return;
    }

    setIsLoadingTitles(true);
    try {
      const res = await fetch(`${API_URL}/crts/autocomplete?q=${encodeURIComponent(inputValue)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      const suggestions = (data.suggestions || []).map(item => ({
        value: item.title,
        label: item.title,
        slug: item.slug || null,
        _id: item._id
      }));

      setTitleSuggestions(suggestions);
    } catch (err) {
      console.error('Autocomplete error:', err);
      setTitleSuggestions([]);
    } finally {
      setIsLoadingTitles(false);
    }
  };

  const debouncedFetchTitles = React.useCallback(
    debounce(fetchTitleSuggestions, 300),
    [token]
  );

  const fetchCrtDetails = async (slug) => {
    try {
      const response = await fetch(`${API_URL}/crts/public/${slug}`);
      if (!response.ok) throw new Error('Failed to fetch CRT details');
      
      const crt = await response.json();
      
      // Pre-fill form with existing data, but leave author fields empty
      setFormData(prev => ({
        ...prev,
        title: crt.title || '',
        chassis: crt.chassis || '',
        viewable_size: crt.viewable_size || '',
        aspect_ratio: crt.aspect_ratio || '',
        chassis_stereo: crt.chassis_stereo || '',
        inputs: crt.inputs || [],
        chroma: crt.chroma || '',
        osd: crt.osd || '',
        // Only take the first tube if it exists
        tubes: crt.tubes && crt.tubes.length > 0 ? [crt.tubes[0].model] : [],
        // Only take the first manual link if it exists
        manual_links: crt.manual_links && crt.manual_links.length > 0 ? [crt.manual_links[0]] : [],
        notes: '' // Leave notes empty for new contribution
      }));
    } catch (error) {
      console.error('Error fetching CRT details:', error);
      setError('Failed to load CRT details. Please try again.');
    }
  };

  const handleTitleChange = async (newValue, actionMeta) => {
    // If a suggestion with a slug was selected, it's an existing CRT
    if (newValue && newValue.slug) {
      setSelectedExistingCrt(newValue);
      await fetchCrtDetails(newValue.slug);
      return;
    }

    const titleValue = newValue ? newValue.value || newValue.label : '';
    setFormData(prev => ({
      ...prev,
      title: titleValue
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tube') {
      setFormData(prev => ({
        ...prev,
        tubes: value ? [value] : []
      }));
    } else if (name === 'manual_url') {
      setFormData(prev => ({
        ...prev,
        manual_links: value ? [{ url: value, type: 'service' }] : []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImagesChange = (e) => {
    if (!turnstileToken) {
      setError('Please complete the verification to upload images');
      return;
    }

    const files = Array.from(e.target.files);
    
    // Check if total images would exceed limit
    if (images.length + files.length > 10) {
      setError('Maximum of 10 images allowed');
      return;
    }

    // Create new image objects and append to existing ones
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setImages(prevImages => [...prevImages, ...newImages]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only check for name and email if user is not logged in
    if (!isAuthenticated && (!formData.author || !formData.author_email)) {
      setError('Please fill in all required fields');
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.title) {
      toast.error('CRT Model Title is required');
      setError('CRT Model Title is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);

    try {
      // First upload images if any
      let uploadedImages = [];
      if (images.length > 0) {
        if (!turnstileToken) {
          const msg = 'Please complete the verification to upload images';
          toast.error(msg);
          throw new Error(msg);
        }

        const formDataObj = new FormData();
        images.forEach(image => {
          formDataObj.append('images', image.file);
        });
        formDataObj.append('turnstileToken', turnstileToken);
        formDataObj.append('slug', tempSlug);

        // Upload images with progress tracking
        uploadedImages = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API_URL}/upload/public/crt-submission`);

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const percent = Math.round((event.loaded / event.total) * 100);
              setUploadProgress(percent);
            }
          };

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              const response = JSON.parse(xhr.responseText);
              resolve(response.images);
            } else {
              reject(new Error(xhr.statusText || 'Upload failed'));
            }
          };

          xhr.onerror = () => {
            reject(new Error('Network error during upload'));
          };

          xhr.send(formDataObj);
        });
      }

      // Submit the CRT data to the products endpoint
      const submissionResponse = await fetch(`${API_URL}/products/public/crt-submission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isAuthenticated && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          crt_name: formData.title,
          author: formData.author,
          author_email: formData.author_email,
          images: uploadedImages,
          source: 'user_submission',
          slug: tempSlug,
          // If this is a contribution to existing CRT
          existing_crt_id: selectedExistingCrt?._id,
          // CRT-specific data
          chassis: formData.chassis,
          chroma: formData.chroma,
          osd: formData.osd,
          tubes: formData.tubes,
          manual_links: formData.manual_links,
          notes: formData.notes,
          viewable_size: formData.viewable_size,
          chassis_stereo: formData.chassis_stereo,
          inputs: formData.inputs,
          // Add CRT model reference if contributing to existing
          crt_models: selectedExistingCrt ? [{
            _id: selectedExistingCrt._id,
            slug: selectedExistingCrt.slug,
            title: selectedExistingCrt.label
          }] : []
        }),
      });

      if (!submissionResponse.ok) {
        throw new Error('Failed to submit CRT data');
      }

      setSuccess(true);
      setShowThankYou(true);
      setTimeout(() => {
        onHide();
      }, 2000);

    } catch (err) {
      console.error('Submission error:', err);
      const errorMessage = err.message || 'Failed to submit CRT data';
      setError(errorMessage);
      toast.error(errorMessage);
      // Reset turnstile on error
      if (window.turnstile) {
        window.turnstile.reset();
      }
      setTurnstileToken(null);
      setUploadProgress(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setImages(images => {
      URL.revokeObjectURL(images[indexToRemove].preview);
      return images.filter((_, index) => index !== indexToRemove);
    });
  };

  React.useEffect(() => {
    return () => {
      images.forEach(image => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  // Reset form when modal is closed
  const handleClose = () => {
    setFormData(initialFormState);
    onHide();
  };

  useEffect(() => {
    // If existingCrtId is provided, we're in contribution mode
    if (existingCrtId) {
      setIsContribution(true);
      setSelectedExistingCrt({
        _id: existingCrtId,
        slug: existingCrtSlug,
        label: existingCrtTitle,
        value: existingCrtTitle
      });
    } else {
      setIsContribution(false);
    }
  }, [existingCrtId, existingCrtSlug, existingCrtTitle]);

  const MODAL_TITLES = {
    help: 'Ask for Modding Help',
    contribution: 'Contribute to Existing CRT',
    new: 'Submit New CRT'
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onExited={() => {
        setSuccess(false);
        setShowThankYou(false);
        setImages([]);
        setTurnstileToken(null);
        setUploadProgress(0);
        setSelectedExistingCrt(null);
      }}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FaTv style={{ verticalAlign: 'middle', marginRight: '8px', marginBottom: '2px' }} />
          {MODAL_TITLES[submissionType] || MODAL_TITLES.new}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && !showThankYou && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        {success && showThankYou && (
          <Alert variant="success">
            Thank you for your {isContribution ? 'contribution' : 'submission'}! It will be reviewed by our team before being added to the database.
          </Alert>
        )}
        {showThankYou ? null : (
          <>
            {uploadProgress > 0 && uploadProgress < 100 && (
              <ProgressBar
                now={uploadProgress}
                label={`${uploadProgress}%`}
                className="mb-3"
              />
            )}
            <Form onSubmit={handleSubmit}>
              {isAuthenticated ? (
                <>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Name * (for credit)</Form.Label>
                        <Form.Control
                          type="text"
                          value={name}
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Email * (not displayed publicly)</Form.Label>
                        <Form.Control
                          type="email"
                          value="Hidden"
                          disabled
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Name * (for credit)</Form.Label>
                        <Form.Control
                          type="text"
                          name="author"
                          value={formData.author}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Your Email * (not displayed publicly)</Form.Label>
                        <Form.Control
                          type="email"
                          name="author_email"
                          value={formData.author_email}
                          onChange={handleInputChange}
                          required
                          disabled={isSubmitting}
                          placeholder="Required to ensure we can contact you!"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}

              {!imageOnly && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>CRT Model Title *</Form.Label>
                    <Select
                      isClearable={!isContribution}
                      isSearchable={!isContribution}
                      isLoading={isLoadingTitles}
                      options={titleSuggestions}
                      value={formData.title ? { value: formData.title, label: formData.title } : null}
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change' && !isContribution) {
                          debouncedFetchTitles(inputValue);
                          if (selectedExistingCrt) {
                            setSelectedExistingCrt(null);
                          }
                        }
                      }}
                      onChange={handleTitleChange}
                      isDisabled={isContribution}
                      placeholder={isContribution ? "Contributing to existing CRT" : "Search or enter CRT model (e.g., Sony KV-27FV300)"}
                      noOptionsMessage={({ inputValue }) => 
                        inputValue.length < 2 
                          ? "Type to search existing models..."
                          : "Can't find your model? Just type it in!"
                      }
                      createOptionPosition="first"
                      formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                      classNamePrefix="dark-select"
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: '#1a0933',
                          borderColor: '#4a4a4a',
                          '&:hover': {
                            borderColor: '#6a6a6a'
                          }
                        }),
                        input: (provided) => ({
                          ...provided,
                          color: 'white'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isFocused ? '#2a1245' : '#1a0933',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#fca311'
                          }
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: 'white'
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: '#1a0933',
                          border: '1px solid #4a4a4a'
                        })
                      }}
                    />
                    <Form.Text className="text-muted">
                      {isContribution 
                        ? "You're contributing additional information to an existing CRT entry"
                        : "Search for existing models or enter a new one"}
                    </Form.Text>
                  </Form.Group>

                  {selectedExistingCrt && !isContribution && (
                    <Alert 
                      variant="info" 
                      className="mb-3"
                    >
                      <p>
                        We already have information about <a href={`/crts/${selectedExistingCrt.slug}`} target="_blank" rel="noopener noreferrer">{selectedExistingCrt.label}</a> in our database. You can still contribute by:
                      </p>
                      <ul>
                        <li>Adding new images</li>
                        <li>Providing corrections or additional technical details</li>
                      </ul>
                      <p className="mb-2">
                        <strong>Note:</strong> Since this is a contribution to an existing entry, you'll receive partial credit if your submission is approved.
                      </p>
                      <div className="d-flex flex-column gap-2">
                        <Button 
                          variant="outline-primary"
                          onClick={() => {
                            // Clear the selection if they want to submit a different CRT
                            setSelectedExistingCrt(null);
                            setFormData(initialFormState);
                          }}
                        >
                          Submit a Different CRT Instead
                        </Button>
                      </div>
                    </Alert>
                  )}

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Chassis (optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="chassis"
                          value={formData.chassis}
                          onChange={handleInputChange}
                          placeholder="Enter chassis details"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Viewable Size</Form.Label>
                        <Select
                          options={viewableSizeOptions}
                          value={viewableSizeOptions.find(option => option.value === formData.viewable_size)}
                          onChange={(option) => handleSelectChange('viewable_size', option?.value)}
                          placeholder="Select size..."
                          isClearable
                          classNamePrefix="dark-select"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Chroma (optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="chroma"
                          value={formData.chroma}
                          onChange={handleInputChange}
                          placeholder="Enter chroma/jungle IC model #"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>OSD (optional)</Form.Label>
                        <Form.Control
                          type="text"
                          name="osd"
                          value={formData.osd}
                          onChange={handleInputChange}
                          placeholder="Enter OSD model #"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Existing Video Inputs</Form.Label>
                        <Select
                          isMulti
                          options={inputOptions}
                          value={inputOptions.filter(option => (formData.inputs || []).includes(option.value))}
                          onChange={(options) => handleSelectChange('inputs', options?.map(opt => opt.value) || [])}
                          placeholder="Select inputs..."
                          classNamePrefix="dark-select"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Stereo Audio</Form.Label>
                        <div className="mt-2 toggle-container" style={{width:'100%'}}>
                          <Form.Check 
                            type="switch"
                            id="chassis-stereo-toggle"
                            checked={formData.chassis_stereo === 'true' || formData.chassis_stereo === true}
                            onChange={(e) => handleSelectChange('chassis_stereo', e.target.checked ? 'true' : 'false')}
                            className="mb-0"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Tube Model (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="tube"
                      value={formData.tubes[0] || ''}
                      onChange={handleInputChange}
                      placeholder="Enter tube model number"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Service Manual URL (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="manual_url"
                      value={formData.manual_links[0]?.url || ''}
                      onChange={handleInputChange}
                      placeholder="Enter service manual URL"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                      placeholder="Enter any additional notes. If you are looking for help with RGB modding, please fill out as much details as possible. Make sure to include your email so that I can contact you."
                    />
                  </Form.Group>
                </>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Upload Images *</Form.Label>
                <div className="mb-2">
                  <Turnstile
                    sitekey={TURNSTILE_SITE_KEY}
                    onVerify={(token) => setTurnstileToken(token)}
                    className="turnstile-wrapper"
                  />
                </div>
                <DragDropImageUpload
                  onImageSelect={handleImagesChange}
                  maxFiles={10}
                  acceptedFileTypes={['image/jpeg', 'image/png']}
                  disabled={isSubmitting || !turnstileToken}
                />
                <Form.Text className="text-muted">
                  Upload up to 10 images. Accepted formats: JPG, PNG. 
                </Form.Text>
              </Form.Group>

              {/* Image Previews */}
              {images.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>Selected Images</Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {images.map((image, index) => (
                      <div
                        key={index}
                        className="position-relative"
                        style={{
                          width: '150px',
                          height: '150px',
                          border: '1px solid var(--bs-border-color)',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}
                      >
                        <img
                          src={image.preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeImage(index)}
                          disabled={isSubmitting}
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            padding: '4px 8px',
                            borderRadius: '50%'
                          }}
                        >
                          <FaTrash size={12} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Form.Group>
              )}

              <div className="d-flex justify-content-end">
                <Button variant="secondary" onClick={handleClose} className="me-2" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  style={{ backgroundColor: '#fca311', color: '#000', border: 'none' }}
                  onClick={handleSubmit}
                >
                  <FaTv style={{ verticalAlign: 'middle', marginRight: '8px', marginBottom: '2px' }} />
                  {isSubmitting ? 'Submitting...' : (isContribution ? 'Submit Contribution' : 'Submit')}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default CRTSubmissionModal; 